import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { ApiError } from '@/api/client';
import { submitStudioAddRequest } from '@/api/studio-add-requests';
import { AppModal } from '@/components/ui/AppModal';

const ADD_STUDIO_MODAL_TITLE_ID = 'add-studio-modal-title';

interface AddStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  aiIdPrefix?: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export function AddStudioModal({
  isOpen,
  onClose,
  aiIdPrefix = 'add-studio-modal',
}: AddStudioModalProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [message, setMessage] = useState('');
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const isDirty = useMemo(() => {
    return name.trim().length > 0 || address.trim().length > 0 || extraInfo.trim().length > 0;
  }, [address, extraInfo, name]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setAddress('');
      setExtraInfo('');
      setStatus('idle');
      setMessage('');
      return undefined;
    }

    const timeoutId = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  function clearSubmitFeedback() {
    if (status !== 'idle') {
      setStatus('idle');
      setMessage('');
    }
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
    setName(event.target.value);
    clearSubmitFeedback();
  }

  function handleAddressChange(event: ChangeEvent<HTMLInputElement>) {
    setAddress(event.target.value);
    clearSubmitFeedback();
  }

  function handleExtraInfoChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setExtraInfo(event.target.value);
    clearSubmitFeedback();
  }

  function handleRequestClose() {
    if (status === 'submitting') {
      return;
    }

    if (isDirty && !window.confirm('작성 중인 연습실 정보가 사라져요. 닫을까요?')) {
      return;
    }

    onClose();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedAddress = address.trim();
    const trimmedExtraInfo = extraInfo.trim();

    if (!trimmedName) {
      setStatus('error');
      setMessage('이름을 입력해주세요.');
      return;
    }

    if (!trimmedAddress) {
      setStatus('error');
      setMessage('주소를 입력해주세요.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const response = await submitStudioAddRequest({
        name: trimmedName,
        address: trimmedAddress,
        extraInfo: trimmedExtraInfo || undefined,
      });

      setName('');
      setAddress('');
      setExtraInfo('');
      setStatus('success');
      setMessage(response.message || '연습실 추가 문의가 접수되었습니다.');
    } catch (error) {
      setStatus('error');
      setMessage(getSubmitErrorMessage(error));
    }
  }

  return (
    <AppModal
      open={isOpen}
      onRequestClose={handleRequestClose}
      titleId={ADD_STUDIO_MODAL_TITLE_ID}
      aiIdPrefix={aiIdPrefix}
    >
      <div className="max-h-[88vh] overflow-y-auto px-5 py-5" data-ai-id={`${aiIdPrefix}-content`}>
        <div className="flex items-start justify-between gap-4" data-ai-id={`${aiIdPrefix}-header`}>
          <div data-ai-id={`${aiIdPrefix}-header-copy`}>
            <h2 id={ADD_STUDIO_MODAL_TITLE_ID} className="text-xl font-bold text-slate-950" data-ai-id={`${aiIdPrefix}-title`}>
              <span data-ai-id={`${aiIdPrefix}-title-text`}>연습실 추가 문의</span>
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500" data-ai-id={`${aiIdPrefix}-description`}>
              <span data-ai-id={`${aiIdPrefix}-description-text`}>보내주신 정보는 관리자가 확인한 뒤 연습실 목록에 반영해요.</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleRequestClose}
            disabled={status === 'submitting'}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
            aria-label="연습실 추가 창 닫기"
            data-ai-id={`${aiIdPrefix}-close-button`}
          >
            <span aria-hidden="true" data-ai-id={`${aiIdPrefix}-close-button-icon-text`}>
              ×
            </span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-ai-id={`${aiIdPrefix}-form`}>
          <div data-ai-id={`${aiIdPrefix}-form-fields`}>
            <label htmlFor="add-studio-name-input" className="block" data-ai-id={`${aiIdPrefix}-name-field`}>
              <span className="block text-sm font-semibold text-slate-800" data-ai-id={`${aiIdPrefix}-name-field-label-text`}>
                이름 <span className="text-emerald-600" data-ai-id={`${aiIdPrefix}-name-field-required-text`}>필수</span>
              </span>
              <input
                ref={nameInputRef}
                id="add-studio-name-input"
                type="text"
                value={name}
                onChange={handleNameChange}
                required
                disabled={status === 'submitting'}
                placeholder="예: 라틴플레이스 연습실"
                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:opacity-70"
                data-ai-id={`${aiIdPrefix}-name-field-input`}
              />
            </label>
          </div>

          <div data-ai-id={`${aiIdPrefix}-address-field-wrapper`}>
            <label htmlFor="add-studio-address-input" className="block" data-ai-id={`${aiIdPrefix}-address-field`}>
              <span className="block text-sm font-semibold text-slate-800" data-ai-id={`${aiIdPrefix}-address-field-label-text`}>
                주소 <span className="text-emerald-600" data-ai-id={`${aiIdPrefix}-address-field-required-text`}>필수</span>
              </span>
              <input
                id="add-studio-address-input"
                type="text"
                value={address}
                onChange={handleAddressChange}
                required
                disabled={status === 'submitting'}
                placeholder="예: 서울 강남구 ..."
                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-base text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:opacity-70"
                data-ai-id={`${aiIdPrefix}-address-field-input`}
              />
            </label>
          </div>

          <div data-ai-id={`${aiIdPrefix}-extra-info-field-wrapper`}>
            <label htmlFor="add-studio-extra-info-input" className="block" data-ai-id={`${aiIdPrefix}-extra-info-field`}>
              <span className="block text-sm font-semibold text-slate-800" data-ai-id={`${aiIdPrefix}-extra-info-field-label-text`}>
                기타정보{' '}
                <span className="font-normal text-slate-400" data-ai-id={`${aiIdPrefix}-extra-info-field-optional-text`}>
                  선택
                </span>
              </span>
              <textarea
                id="add-studio-extra-info-input"
                value={extraInfo}
                onChange={handleExtraInfoChange}
                rows={4}
                disabled={status === 'submitting'}
                placeholder="예약 링크, 연락처, 운영시간 등 참고할 내용을 자유롭게 적어주세요."
                className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base leading-6 text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:opacity-70"
                data-ai-id={`${aiIdPrefix}-extra-info-field-textarea`}
              />
            </label>
          </div>

          {message ? (
            <p
              className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
                status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
              role={status === 'error' ? 'alert' : 'status'}
              data-ai-id={status === 'success' ? `${aiIdPrefix}-submit-success-message` : `${aiIdPrefix}-submit-error-message`}
            >
              <span data-ai-id={`${aiIdPrefix}-submit-message-text`}>{message}</span>
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-2 pt-1" data-ai-id={`${aiIdPrefix}-form-actions`}>
            <button
              type="button"
              onClick={handleRequestClose}
              disabled={status === 'submitting'}
              className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
              data-ai-id={`${aiIdPrefix}-cancel-button`}
            >
              <span data-ai-id={`${aiIdPrefix}-cancel-button-label-text`}>{status === 'success' ? '닫기' : '취소'}</span>
            </button>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="h-11 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-70 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
              data-ai-id={`${aiIdPrefix}-submit-button`}
            >
              <span data-ai-id={`${aiIdPrefix}-submit-button-label-text`}>
                {status === 'submitting' ? '접수 중' : '문의 접수하기'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  );
}

function getSubmitErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return '연습실 추가 문의를 접수하지 못했어요. 잠시 후 다시 시도해주세요.';
}
